import type {
    SponsoredBillingStatus,
    SponsoredCreateResult,
    SponsoredFailReason,
    SponsoredListResponse,
    SponsoredLookupResult,
    SponsoredMutateResult,
    SponsoredStatus,
    SponsoredSubscription,
    SponsoredTarget,
} from "./types";

// In-memory backend used with VITE_FAKE_SPONSORED_SUBS. Mutations live for the
// session so add / cancel / reactivate can be exercised without a server.

const DAY = 86_400_000;
const PRICE_CENTS = 999;

type FakeState = {
    subs: SponsoredSubscription[];
    billing: SponsoredBillingStatus;
};
const stores = new Map<string, FakeState>();

function hash(s: string): number {
    let h = 2166136261;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}

function rng(seed: number) {
    let a = seed;
    return () => {
        a |= 0;
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

const NAMES = [
    "ana", "marc", "laura", "joan", "sara", "pau", "marta", "david",
    "elena", "oriol", "nuria", "carlos", "irene", "ruben", "alba", "hugo",
];
const DOMAINS = ["gmail.com", "hotmail.com", "outlook.com", "icloud.com"];

export function maskEmail(email: string): string {
    const e = (email || "").trim();
    const at = e.indexOf("@");
    if (at <= 1) return e || "—";
    return `${e.slice(0, 2)}***@${e.slice(at + 1)}`;
}

function makeSub(
    id: string,
    userId: string | null,
    emailMasked: string,
    status: SponsoredStatus,
    startedAt: number,
    now: number,
): SponsoredSubscription {
    const cycle = 30 * DAY;
    const sinceStart = Math.max(0, now - startedAt);
    const periodEnd = startedAt + (Math.floor(sinceStart / cycle) + 1) * cycle;
    return {
        id,
        stripeSubscriptionId: `sub_fake_${id}`,
        userId,
        emailMasked,
        status,
        startedAt,
        currentPeriodEnd: periodEnd,
        cancelAtPeriodEnd: status === "canceling",
        canceledAt: status === "canceling" ? now - 2 * DAY : null,
        amount: { amount: PRICE_CENTS, currency: "eur" },
        interval: "month",
        latestInvoiceUrl: null,
    };
}

function seed(uid: string): FakeState {
    const r = rng(hash(uid));
    const now = Date.now();
    const n = 6 + Math.floor(r() * 7);
    const subs: SponsoredSubscription[] = [];
    for (let i = 0; i < n; i++) {
        const name = NAMES[Math.floor(r() * NAMES.length)];
        const email = `${name}${Math.floor(r() * 90) + 10}@${DOMAINS[Math.floor(r() * DOMAINS.length)]}`;
        const p = r();
        const status: SponsoredStatus =
            p < 0.7 ? "active" : p < 0.85 ? "canceling" : "past_due";
        const started = now - Math.floor(r() * 200 + 5) * DAY;
        subs.push(
            makeSub(`fake${i}`, `fake-user-${i}`, maskEmail(email), status, started, now),
        );
    }
    // The fake partner starts with a card saved so the list is useful at once.
    return {
        subs,
        billing: {
            hasCustomer: true,
            hasDefaultPaymentMethod: true,
            paymentMethod: {
                type: "card",
                brand: "visa",
                last4: "4242",
                expMonth: 12,
                expYear: 2030,
            },
        },
    };
}

function state(uid: string): FakeState {
    let s = stores.get(uid);
    if (!s) {
        s = seed(uid);
        stores.set(uid, s);
    }
    return s;
}

const delay = () =>
    new Promise((r) => setTimeout(r, 300 + Math.random() * 300));

function totalsFor(subs: SponsoredSubscription[]) {
    const live = subs.filter((s) => s.status !== "canceled");
    return {
        active: live.length,
        canceling: subs.filter((s) => s.status === "canceling").length,
        pastDue: subs.filter((s) => s.status === "past_due").length,
        monthlyCost: {
            amount: live.reduce((a, s) => a + s.amount.amount, 0),
            currency: "eur",
        },
    };
}

export async function fakeList(uid: string): Promise<SponsoredListResponse> {
    await delay();
    const s = state(uid);
    return {
        items: [...s.subs].sort((a, b) => b.startedAt - a.startedAt),
        totals: totalsFor(s.subs),
        billing: { ...s.billing },
    };
}

export async function fakeBilling(uid: string) {
    await delay();
    return { ...state(uid).billing };
}

/** "Saves" a card (as if Checkout setup had completed). */
export async function fakeSetupSession(uid: string, successPath: string) {
    await delay();
    const s = state(uid);
    s.billing = {
        hasCustomer: true,
        hasDefaultPaymentMethod: true,
        paymentMethod: {
            type: "card",
            brand: "visa",
            last4: "4242",
            expMonth: 12,
            expYear: 2030,
        },
    };
    return { url: successPath };
}

function judge(target: SponsoredTarget, uid: string): SponsoredFailReason | null {
    if ("email" in target) {
        const e = target.email.trim().toLowerCase();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return "invalid_email";
        if (e.includes("nobody")) return "user_not_found";
        if (e.includes("premium")) return "already_premium";
        if (e.includes("other")) return "linked_to_other_partner";
    }
    const s = state(uid);
    const key = "userId" in target ? target.userId : maskEmail(target.email);
    if (
        s.subs.some(
            (x) =>
                x.status !== "canceled" &&
                (x.userId === key || x.emailMasked === key),
        )
    )
        return "already_sponsored";
    return null;
}

export async function fakeLookup(
    uid: string,
    target: SponsoredTarget,
): Promise<SponsoredLookupResult> {
    await delay();
    const reason = judge(target, uid);
    if (reason) return { ok: false, reason };
    if ("userId" in target) {
        return {
            ok: true,
            userId: target.userId,
            emailMasked: "••***@••",
            linkState: "linked_to_you",
            price: { amount: PRICE_CENTS, currency: "eur", interval: "month" },
        };
    }
    return {
        ok: true,
        userId: `fake-${hash(target.email)}`,
        emailMasked: maskEmail(target.email),
        linkState: target.email.includes("new") ? "unlinked" : "linked_to_you",
        price: { amount: PRICE_CENTS, currency: "eur", interval: "month" },
    };
}

export async function fakeCreate(
    uid: string,
    target: SponsoredTarget,
): Promise<SponsoredCreateResult> {
    await delay();
    const s = state(uid);
    if (!s.billing.hasDefaultPaymentMethod)
        return { ok: false, reason: "no_payment_method" };
    const reason = judge(target, uid);
    if (reason) return { ok: false, reason };
    const now = Date.now();
    const userId = "userId" in target ? target.userId : `fake-${hash(target.email)}`;
    const email = "email" in target ? maskEmail(target.email) : `${userId.slice(0, 2)}***@caloo`;
    const sub = makeSub(`fake${now}`, userId, email, "active", now, now);
    s.subs.push(sub);
    return {
        ok: true,
        subscription: sub,
        linkedNow: "email" in target && target.email.includes("new"),
    };
}

function mutate(
    uid: string,
    id: string,
    fn: (s: SponsoredSubscription) => SponsoredSubscription,
): SponsoredMutateResult {
    const st = state(uid);
    const i = st.subs.findIndex((x) => x.id === id);
    const cur = st.subs[i];
    if (i < 0 || !cur) return { ok: false, reason: "unknown" };
    const next = fn(cur);
    st.subs[i] = next;
    return { ok: true, subscription: next };
}

export async function fakeCancel(uid: string, id: string) {
    await delay();
    return mutate(uid, id, (s) => ({
        ...s,
        status: "canceling",
        cancelAtPeriodEnd: true,
        canceledAt: Date.now(),
    }));
}

export async function fakeReactivate(uid: string, id: string) {
    await delay();
    return mutate(uid, id, (s) => ({
        ...s,
        status: "active",
        cancelAtPeriodEnd: false,
        canceledAt: null,
    }));
}
